import React, { useState } from 'react';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BookingDetails } from '@/hooks/booking/useBookingDetails';
import { BookingHeader } from './BookingHeader';
import { BookingTimeline } from './BookingTimeline';
import { CustomerInfoCard } from './CustomerInfoCard';
import { BookingDetailsSection } from './BookingDetailsSection';
import { BookingActions } from './BookingActions';

interface BookingDetailsLayoutProps {
  booking: BookingDetails;
  onBack: () => void;
  onRefresh: () => void;
}

export const BookingDetailsLayout = ({ booking, onBack, onRefresh }: BookingDetailsLayoutProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await onRefresh();
    setIsRefreshing(false);
  };

  return (
    <>
      {/* Fixed Header */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="flex items-center justify-between p-4">
          <Button variant="ghost" size="sm" onClick={onBack} className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-4 space-y-6">
          <BookingHeader booking={booking} />
          <BookingTimeline booking={booking} />
          <CustomerInfoCard booking={booking} />
          <BookingDetailsSection booking={booking} />
        </div>
      </div>

      {/* Sticky Bottom Actions */}
      <div className="sticky bottom-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t p-4">
        <div className="max-w-4xl mx-auto">
          <BookingActions booking={booking} onSuccess={handleRefresh} />
        </div>
      </div>
    </>
  );
};